import { defineStore } from "pinia";
import router from "@/router";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useCurrentStore } from "@/stores/current";
import { useErrorStore } from "@/stores/error.store";
import { useUserStore } from "@/stores/user.store"; // ⬅️ do pobrania updated_at

const INACTIVITY_TIMEOUT_MS = 21 * 60 * 1000;

// Store Pinia odpowiadający za uwierzytelnianie użytkownika oraz sesję
export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    token: sessionStorage.getItem("cvToken"), // Token JWT
    returnUrl: "", // Redirect po logowaniu
    pingLoop: false, // ID interwału pingowania
    current: null, // opcjonalnie bieżący user
    mustChangePassword: sessionStorage.getItem("mustChangePwd") === "1", // <---
    useErrorStore: useErrorStore(),
    skipPwdFreshCheckOnce: false,   // przepuść najbliższą nawigację bez sprawdzania ważności
    localPasswordChangedAt: null,   // lokalny „znacznik czasu” zmiany hasła
    suspendHeartbeatsUntilUserAction: false,
  }),

  actions: {
    // helper
    _setMustChangePassword(val) {
      this.mustChangePassword = !!val;
      if (val) sessionStorage.setItem("mustChangePwd", "1");
      else sessionStorage.removeItem("mustChangePwd");
    },

    // 90 dni
    _isPasswordExpired(updatedAt) {
      if (!updatedAt) return true;
      const ts = new Date(updatedAt).getTime();
      return Date.now() - ts > 90 * 24 * 60 * 60 * 1000;
    },

    // Logowanie użytkownika - pobiera token, zapisuje go do sessionStorage
    async login(username, password) {
      try {
        // 1) /login
        const authRes = await fetchWrapper.post(
          `${process.env.VUE_APP_API_URL}/login`,
          { username, password }
        );

        // 2) zapisz token
        this.token = authRes.token;
        sessionStorage.setItem("cvToken", authRes.token);
        this.useErrorStore.clearErrorMessage();

        // 3) 🔍 po zalogowaniu – sprawdź ważność hasła wg updated_at
        const passwordExpired = await this.ensurePasswordFreshness();

        // Jeśli hasło nieważne — kierujemy na zmianę hasła (użyj właściwej trasy!)
        if (passwordExpired) {
          // Pokaż komunikat (opcjonalnie)
          this.useErrorStore.showErrorMessage(
            "Twoje hasło straciło ważność. Ustaw nowe hasło, aby kontynuować."
          );
          // 🔁 przekieruj na ekran zmiany hasła / preferencji (DOPASUJ TRASĘ U SIEBIE)
          router.push("/password-expired"); // <-- jeżeli masz inną trasę do zmiany hasła, podmień to
        } else {
          // 4) uruchom nasłuch bezczynności i ping
          this.startInactivityWatch();
          this.ping(false);

          // 5) przekierowanie na docelową stronę
          router.push(
            typeof this.returnUrl === "string" && this.returnUrl.length > 0
              ? this.returnUrl
              : "/"
          );
        }
      } catch (error) {
        this.useErrorStore.showErrorMessage(error);
      }
    },

    // 🔍 Sprawdź ważność hasła (3 miesiące od updated_at)
    // async ensurePasswordFreshness() {
    //   try {
    //     const userStore = useUserStore();
    //     if (typeof userStore.self === "function") {
    //       await userStore.self();
    //     }

    //     const user = userStore.currentUser || {};

    //     console.log('user:', JSON.stringify(user));
    //     // Elastycznie: bierzemy cokolwiek serwer zwraca
    //     const updatedAtRaw = user.passwordUpdatedAt || user.updatedAt;

    //     if (!updatedAtRaw) {
    //       // jeśli nie mamy daty – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     const updatedAt = new Date(updatedAtRaw);
    //     if (isNaN(updatedAt.getTime())) {
    //       // jeśli data niepoprawna – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     // 3 miesiące od updated_at
    //     const expiry = new Date(updatedAt.getTime());
    //     expiry.setMonth(expiry.getMonth() + 3);

    //     return new Date() > expiry; // true => hasło wygasło
    //   } catch {
    //     // w razie błędu nie blokuj użytkownika – traktuj jako ważne
    //     return false;
    //   }
    // },

    async ensurePasswordFreshness() {
      try {
        // 0) Jednorazowa amnestia zaraz po zmianie hasła
        if (this.skipPwdFreshCheckOnce) {
          this.skipPwdFreshCheckOnce = false;
          return false; // NIE traktuj jako wygasłe
        }
    
        const userStore = useUserStore();
        if (typeof userStore.self === "function") {
          await userStore.self();
        }
        const u = userStore.currentUser || {};
    
        // helper do parsowania daty
        const asDate = (v) => {
          if (!v) return null;
          const d = new Date(v);
          return isNaN(d) ? null : d;
        };
    
        // 1) Jeśli mamy lokalny znacznik zmiany – użyj go (eliminuje lag backendu)
        let changedAt =
          (this.localPasswordChangedAt && new Date(this.localPasswordChangedAt)) ||
          asDate(u.passwordUpdatedAt) ||
          asDate(u.password_updated_at) ||
          asDate(u.updatedAt) ||
          asDate(u.updated_at);
    
        // 2) Fallback – jeśli mamy passwordExpiration, to też możemy użyć
        const explicitExpiration =
          asDate(u.passwordExpiration) || asDate(u.password_expiration);
    
        if (changedAt) {
          const expiry = new Date(changedAt.getTime());
          expiry.setMonth(expiry.getMonth() + 3); // 3 miesiące ważności
          return new Date() > expiry;
        }
    
        if (explicitExpiration) {
          return new Date() > explicitExpiration;
        }
    
        // Brak jakichkolwiek dat => nie blokuj
        return false;
      } catch {
        // W razie błędu nie blokuj
        return false;
      }
    },

    // Rozpoczyna cykliczne pingowanie API (utrzymanie sesji)
    async ping(disable) {
      if (disable) {
        clearInterval(this.pingLoop);
        this.pingLoop = false;
      } else {
        if (!this.pingLoop) {
          this.pingLoop = setInterval(() => {
            fetchWrapper.get(`${process.env.VUE_APP_API_URL}/ping`);
          }, 20000);
        }
      }
    },

    // 🔒 Start/Stop auto-logout po 5 min braku aktywności
    // startInactivityWatch() {
    //   this.stopInactivityWatch(); // safety
    //   this._activityEvents = [
    //     "mousemove",
    //     "keydown",
    //     "click",
    //     "touchstart",
    //     "scroll",
    //   ];
    //   this._boundActivityHandler = () => this.resetInactivityTimer();
    //
    //   this._activityEvents.forEach((evt) => {
    //     window.addEventListener(evt, this._boundActivityHandler, {
    //       passive: true,
    //     });
    //   });
    //
    //   this.resetInactivityTimer();
    // },
    //
    // stopInactivityWatch() {
    //   if (this._activityEvents && this._boundActivityHandler) {
    //     this._activityEvents.forEach((evt) => {
    //       window.removeEventListener(evt, this._boundActivityHandler);
    //     });
    //   }
    //   this._activityEvents = null;
    //   this._boundActivityHandler = null;
    //
    //   if (this.inactivityTimer) {
    //     clearTimeout(this.inactivityTimer);
    //     this.inactivityTimer = null;
    //   }
    // },

    // resetInactivityTimer() {
    //   if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    //   // 5 minut = 300000 ms
    //   this.inactivityTimer = setTimeout(() => {
    //     this.useErrorStore.showErrorMessage(
    //       "Zostałeś wylogowany z powodu braku aktywności (5 min)."
    //     );
    //     this.logout();
    //   }, 5 * 60 * 1000);
    // },

    // onPasswordChanged: async function() {
    //   try { 
    //     const userStore = useUserStore();
    //     await userStore.self?.(); // świeży updated_at/passwordUpdatedAt
    //   } catch {
    //     // ignoruj
    //   }
    //   this._setMustChangePassword(false);
    //   this.startInactivityWatch();
    //   this.ping(false);
    // },
      startInactivityWatch() {
          this.stopInactivityWatch(); // safety

          // 1) Prawdziwa aktywność użytkownika = reset licznika
          this._activityEvents = ["mousemove", "keydown", "click", "touchstart", "scroll"];
          this._onUserActivity = () => this.onUserActivity();

          this._activityEvents.forEach((evt) => {
              window.addEventListener(evt, this._onUserActivity, { passive: true });
          });

          // 2) Widoczność karty:
          //    - gdy karta staje się NIEWIDOCZNA: nie resetujemy nic (odliczanie trwa)
          //    - gdy karta WRACA na wierzch: NIE resetujemy automatycznie (czekamy na akcję usera)
          this._onVisibilityChange = () => {
              if (document.hidden) {
                  // karta ukryta — po prostu pozwól timerowi dobiegać
                  return;
              }
              // karta znów widoczna — wstrzymaj “auto-heartbeat” do czasu aż user coś zrobi
              this.suspendHeartbeatsUntilUserAction = true;
          };
          document.addEventListener("visibilitychange", this._onVisibilityChange);

          // 3) start licznika
          this.resetInactivityTimer();
      },

      stopInactivityWatch() {
          if (this._activityEvents && this._onUserActivity) {
              this._activityEvents.forEach((evt) => {
                  window.removeEventListener(evt, this._onUserActivity);
              });
          }
          if (this._onVisibilityChange) {
              document.removeEventListener("visibilitychange", this._onVisibilityChange);
          }
          this._activityEvents = null;
          this._onUserActivity = null;
          this._onVisibilityChange = null;

          if (this.inactivityTimer) {
              clearTimeout(this.inactivityTimer);
              this.inactivityTimer = null;
          }
      },

      onUserActivity() {
          // User zrobił “coś” po powrocie do karty — odblokuj auto-heartbeat i zresetuj licznik
          this.suspendHeartbeatsUntilUserAction = false;
          this.resetInactivityTimer();
      },

      resetInactivityTimer() {
          if (this.inactivityTimer) clearTimeout(this.inactivityTimer);

          this.inactivityTimer = setTimeout(() => {
              this.useErrorStore.showErrorMessage("Zostałeś wylogowany z powodu braku aktywności (21 min).");
              this.logout();
          }, INACTIVITY_TIMEOUT_MS);
      },

      /**
       * “Bicie serca” z aplikacji (np. z komponentu bieżącego pomiaru).
       * Wywołuj kiedy aplikacja “żyje”, ale tylko jeśli karta jest widoczna
       * i nie czekamy na pierwszą akcję usera po powrocie do karty.
       */
      heartbeat(source = "app") {
          console.log(source);
          if (!document.hidden && !this.suspendHeartbeatsUntilUserAction) {
              this.resetInactivityTimer();
          }
      },
    onPasswordChanged: async function() {
      // 1) zdejmij wymuszanie zmiany hasła
      this._setMustChangePassword(false);
    
      // 2) ustaw lokalny „czas zmiany” i jednorazowe pominięcie sprawdzania
      this.localPasswordChangedAt = new Date();
      this.skipPwdFreshCheckOnce = true;
    
      // 3) spróbuj dociągnąć świeżego usera (jak backend już zaktualizował daty)
      try {
        const userStore = useUserStore();
        await userStore.self?.();
      } catch {
        console.log('onPasswordChanged: nie udało się pobrać danych użytkownika');
      }
    
      // 4) wznów zegary sesji
      this.startInactivityWatch();
      this.ping(false);
    },    

    // Wylogowuje użytkownika (usuwa token, czyści wszystko, przekierowuje na /login)
    logout() {
      const current = useCurrentStore();
      current.stopLogout(); // czyści interwały i dane bieżące

      // zatrzymaj ping i nasłuch bezczynności
      this.ping(true);
      this.stopInactivityWatch();

      fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/login`);
      this.token = null;
      this.pingLoop = false;
      sessionStorage.removeItem("cvToken");
      router.push("/login");
    },

    // Resetowanie hasła (wysyłka maila z linkiem)
    async resetPassword(email) {
      try {
        await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/reset-password`,
          { email }
        );
        router.push({
          path: `/reset-password/reset/${encodeURIComponent(email)}`,
        });
      } catch (error) {
        this.useErrorStore.showErrorMessage(error);
      }
    },

    // Ustawienie nowego hasła po wejściu z linka z maila (token z URL)
    async setPassword(email, password, token) {
      try {
        const response = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/set-password`,
          { email, password, token }
        );
        return response;
      } catch (error) {
        console.log("Error in setPassword:", error);
        return error;
      }
    },

    // Czyści komunikat o błędzie (np. po wyświetleniu na UI)
    clearErrorMessage() {
      this.useErrorStore.clearErrorMessage();
    },
  },
});
